import type { NearbyOfficeOption } from "@homeinstead/marketing-api";

import { assign, setup } from "xstate";

type OfficeSearchContext = {
  hasLookupError: boolean;
  isFetching: boolean;
  options: NearbyOfficeOption[];
  /**
   * A restored office id to prefer over the nearest office once the lookup
   * produces a real result set. Cleared as soon as it has been checked
   * against one (see SYNC_LOOKUP), so it never overrides a later manual
   * selection.
   */
  preferredOfficeId: number | null;
  /**
   * The restored office's postal code. One franchise can return several
   * options with the same office id, so the id alone is not the location
   * the visitor picked.
   */
  preferredPostalCode: string | null;
  selectedOptionId: string | null;
  typedLocation: string;
};

type OfficeSearchEvent =
  | { type: "TYPE"; value: string }
  | {
      type: "SYNC_LOOKUP";
      options: NearbyOfficeOption[];
      isFetching: boolean;
      hasLookupError: boolean;
    }
  | { type: "SELECT_OPTION"; optionId: string };

type OfficeSearchInitialContext = {
  preferredOfficeId: number | null;
  preferredPostalCode: string | null;
  typedLocation: string;
};

/**
 * The option for a restored office. A postal code picks that location when
 * the franchise has several; without one, the first option for the office
 * is enough. A postal code that is no longer in the results matches nothing,
 * and the caller falls back to the nearest office.
 */
function findRestoredOption(
  options: NearbyOfficeOption[],
  preferredOfficeId: number | null,
  preferredPostalCode: string | null,
): NearbyOfficeOption | undefined {
  if (preferredOfficeId == null) {
    return undefined;
  }

  const sameOrganization = options.filter(
    (option) => option.officeID === preferredOfficeId,
  );

  if (preferredPostalCode == null) {
    return sameOrganization[0];
  }

  return sameOrganization.find(
    (option) => option.address.postalCode === preferredPostalCode,
  );
}

const officeSearchMachine = setup({
  types: {
    context: {} as OfficeSearchContext,
    events: {} as OfficeSearchEvent,
  },
});

/**
 * What the visitor typed, the lookup results for it, and the office picked
 * from them. The nearest office is picked by default, and a restored office
 * wins once a lookup confirms it.
 */
function createOfficeSearchMachine(initial: OfficeSearchInitialContext) {
  return officeSearchMachine.createMachine({
    context: {
      hasLookupError: false,
      isFetching: false,
      options: [],
      preferredOfficeId: initial.preferredOfficeId,
      preferredPostalCode: initial.preferredPostalCode,
      selectedOptionId: null,
      typedLocation: initial.typedLocation,
    },
    id: "officeSearch",
    initial: "ready",
    states: {
      ready: {
        on: {
          SELECT_OPTION: {
            actions: assign({
              selectedOptionId: ({ event }) => event.optionId,
            }),
          },
          SYNC_LOOKUP: {
            actions: assign({
              hasLookupError: ({ event }) => event.hasLookupError,
              isFetching: ({ event }) => event.isFetching,
              options: ({ event }) => event.options,
              // Retained only while the lookup is in flight without a real
              // result set, then cleared whether the check succeeds, falls
              // back, or the lookup ends in an error or no results.
              preferredOfficeId: ({ context, event }) =>
                event.isFetching && event.options.length === 0
                  ? context.preferredOfficeId
                  : null,
              preferredPostalCode: ({ context, event }) =>
                event.isFetching && event.options.length === 0
                  ? context.preferredPostalCode
                  : null,
              selectedOptionId: ({ context, event }) => {
                if (event.options.length === 0) {
                  return null;
                }

                const restoredOption = findRestoredOption(
                  event.options,
                  context.preferredOfficeId,
                  context.preferredPostalCode,
                );
                if (restoredOption) {
                  return restoredOption.optionId;
                }

                const selectedStillExists = event.options.some(
                  (option) => option.optionId === context.selectedOptionId,
                );
                // A manual choice is kept whenever it survives the new
                // result set; otherwise the nearest office is picked.
                return selectedStillExists
                  ? context.selectedOptionId
                  : (event.options[0]?.optionId ?? null);
              },
            }),
          },
          TYPE: {
            actions: assign({
              preferredOfficeId: () => null,
              preferredPostalCode: () => null,
              selectedOptionId: () => null,
              typedLocation: ({ event }) => event.value,
            }),
          },
        },
      },
    },
  });
}

export { createOfficeSearchMachine };
